/* Opuliss carousel page indicator ("bubbles").
 *
 * Shared by every horizontally scrolling product row on the store, so the
 * indicator reads the same everywhere. Requested 2026-08-01 for three rows:
 * the skincare Must Haves band, "Complete Your Regime" on the PDP, and the
 * escin products row.
 *
 * Usage: put data-op-carousel on the element that actually SCROLLS.
 * Optional data-op-carousel-label gives the dots an accessible group name.
 * Optional data-op-carousel-mount is a selector for an ANCESTOR to place the
 * dots after, instead of the scroller itself. Needed where the scroller sits
 * inside a viewport carrying vertically centred arrows: growing that viewport
 * would drag the arrows off centre.
 *
 * Two deliberate behaviours:
 *   - the indicator hides itself when everything already fits, because a
 *     single dot tells the reader nothing and reads as clutter;
 *   - pages are measured from the live card width rather than assumed, so it
 *     stays correct across breakpoints where the card count per view changes.
 *
 * Self-guarding: several sections may each request this file, and the browser
 * will run it once per tag. The flag makes re-entry a no-op.
 */
(function () {
  if (window.__opCarouselDots) return;
  window.__opCarouselDots = true;

  /* 2026-09-26: each dot is now a 24px tap area (the minimum for a finger) with the same 7px dot
     drawn in its centre. Negative vertical margins keep the row 7px tall, so nothing above or below
     moves. Two 24px areas cannot overlap, so the dots now sit 24px apart instead of 15px. */
  var CSS = [
    '.op-dots{display:flex;justify-content:center;align-items:center;gap:0;',
    'margin:18px auto 0;padding:0;list-style:none;line-height:0}',
    '.op-dots[hidden]{display:none}',
    '.op-dots__dot{position:relative;width:24px;height:24px;margin:-8.5px 0;padding:0;border:0;',
    'background:transparent;cursor:pointer;-webkit-appearance:none;appearance:none}',
    '.op-dots__dot::before{content:"";position:absolute;top:50%;left:50%;width:7px;height:7px;',
    'margin:-3.5px 0 0 -3.5px;border-radius:50%;background:currentColor;opacity:.22;',
    'transition:opacity .25s ease,transform .25s ease}',
    '.op-dots__dot:hover::before{opacity:.5}',
    '.op-dots__dot[aria-current="true"]::before{opacity:.85;transform:scale(1.3)}',
    '@media (prefers-reduced-motion:reduce){.op-dots__dot::before{transition:none}}'
  ].join('');

  function injectStyle() {
    if (document.getElementById('op-dots-style')) return;
    var s = document.createElement('style');
    s.id = 'op-dots-style';
    s.textContent = CSS;
    document.head.appendChild(s);
  }

  function pageCount(scroller) {
    // Round rather than floor: a row showing 4 cards plus a sliver of the 5th
    // is a 4-card view, and flooring would report an extra phantom page.
    var w = scroller.clientWidth;
    if (w <= 0) return 1;
    return Math.max(1, Math.ceil(scroller.scrollWidth / w - 0.02));
  }

  function build(scroller) {
    if (scroller.__opDots) return scroller.__opDots;
    var nav = document.createElement('div');
    nav.className = 'op-dots';
    nav.setAttribute('role', 'group');
    nav.setAttribute('aria-label',
      scroller.getAttribute('data-op-carousel-label') || 'Carousel position');
    var mountSel = scroller.getAttribute('data-op-carousel-mount');
    var anchor = mountSel ? (scroller.closest(mountSel) || scroller) : scroller;
    anchor.insertAdjacentElement('afterend', nav);
    scroller.__opDots = nav;
    return nav;
  }

  function sync(scroller) {
    var nav = build(scroller);
    var pages = pageCount(scroller);

    if (pages < 2) {
      nav.hidden = true;
      nav.innerHTML = '';
      return;
    }
    nav.hidden = false;

    if (nav.children.length !== pages) {
      nav.innerHTML = '';
      for (var i = 0; i < pages; i++) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'op-dots__dot';
        b.setAttribute('aria-label', 'Go to item ' + (i + 1) + ' of ' + pages);
        b.dataset.opPage = String(i);
        nav.appendChild(b);
      }
    }

    var current = Math.round(scroller.scrollLeft / Math.max(1, scroller.clientWidth));
    if (current > pages - 1) current = pages - 1;
    for (var j = 0; j < nav.children.length; j++) {
      nav.children[j].setAttribute('aria-current', j === current ? 'true' : 'false');
    }
  }

  function wire(scroller) {
    if (scroller.__opDotsWired) return;
    scroller.__opDotsWired = true;

    var raf = 0;
    scroller.addEventListener('scroll', function () {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(function () { raf = 0; sync(scroller); });
    }, { passive: true });

    build(scroller).addEventListener('click', function (e) {
      var dot = e.target.closest('.op-dots__dot');
      if (!dot) return;
      scroller.scrollTo({
        left: parseInt(dot.dataset.opPage, 10) * scroller.clientWidth,
        behavior: 'smooth'
      });
    });

    // Hold the observer ON the element. An unreferenced ResizeObserver is
    // eligible for collection, and when it goes the indicator silently stops
    // updating: exactly the failure this component was first shipped with.
    if (typeof ResizeObserver === 'function') {
      scroller.__opDotsRO = new ResizeObserver(function () { sync(scroller); });
      scroller.__opDotsRO.observe(scroller);
    }
    // Kept even when ResizeObserver exists, as a cheap second path.
    window.addEventListener('resize', function () { sync(scroller); });
  }

  function init() {
    injectStyle();
    document.querySelectorAll('[data-op-carousel]').forEach(function (s) {
      wire(s);
      sync(s);
    });
  }

  // Re-sync is the whole ballgame. The first measurement can land before the
  // row has its real scrollWidth, and a single early sync reports "everything
  // fits" and hides the indicator permanently. So schedule several, cheaply.
  function initAndSettle() {
    init();
    requestAnimationFrame(function () {
      requestAnimationFrame(init);
    });
    setTimeout(init, 250);
    setTimeout(init, 1200);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAndSettle);
  } else {
    initAndSettle();
  }
  // Only useful if load has NOT already fired. Attaching it unconditionally is
  // what made the first version look wired while never running again.
  if (document.readyState !== 'complete') {
    window.addEventListener('load', initAndSettle);
  }
  // Late images change scrollWidth after everything above has run.
  document.addEventListener('load', function (e) {
    if (e.target && e.target.tagName === 'IMG') init();
  }, true);
  // Shopify theme editor re-renders a section without a page load.
  document.addEventListener('shopify:section:load', initAndSettle);
})();
